import type { IUser } from "@/domains/shared/models/IUser";
import { ClinicRoles } from "@/domains/multi/clinicRoles";
import { normalizePlanSlug, SubscriptionPlanIds } from "@/domains/subscription/plans";

export const THERAPIST_MARKETPLACE_PATH = "/profissional/marketplace";
export const THERAPIST_ADMINISTRATIVE_TEAM_PATH = "/profissional/administrativo";
export const CLINIC_HOME_PATH = "/clinica";
export const CLINIC_SUBSCRIPTION_PATH = "/clinica/assinatura";
export const CLINIC_MARKETPLACE_PATH = "/clinica/marketplace";
export const CLINIC_TEAM_PATH = "/clinica/equipe";

const MULTI_DEFAULT_MAX_PROFESSIONALS = 3;
const SINGLE_PROFESSIONAL_MAX_PROFESSIONALS = 1;

export type SeatCapabilities = {
    maxProfessionals: number;
    allowsSeatAddons: boolean;
    canDissolveOrg: boolean;
    isMultiCapacityPlan: boolean;
};

/**
 * Derives tier feature flags from auth/me plan slug and clinic seat limits.
 * Básico/Pro/Gratuito never receive Multi-only flags even when stale limits suggest more seats.
 * `canDissolveOrg` is always false — there is no Solo product and Dissolver is removed.
 */
export function resolveSeatCapabilities(
    user?: Pick<IUser, "subscriptionPlan" | "clinic"> | null,
): SeatCapabilities {
    const planSlug = normalizePlanSlug(user?.subscriptionPlan);
    const isMultiSlug = planSlug === SubscriptionPlanIds.clinic;
    const allowsSeatAddons = isMultiSlug;
    const canDissolveOrg = false;

    let maxProfessionals: number;
    if (isMultiSlug) {
        const limit = user?.clinic?.professionalSeatLimit;
        if (typeof limit === "number" && Number.isFinite(limit) && limit > 0) {
            maxProfessionals = limit;
        } else {
            maxProfessionals = MULTI_DEFAULT_MAX_PROFESSIONALS;
        }
    } else {
            maxProfessionals = SINGLE_PROFESSIONAL_MAX_PROFESSIONALS;
    }

    const isMultiCapacityPlan = isMultiSlug || allowsSeatAddons || maxProfessionals > 1;

    return {
        maxProfessionals,
        allowsSeatAddons,
        canDissolveOrg,
        isMultiCapacityPlan,
    };
}

/**
 * @deprecated Use {@link resolveSeatCapabilities} for feature gates. Slug-only Multi check.
 */
export function hasClinicSubscriptionPlan(user?: IUser | null): boolean {
    return normalizePlanSlug(user?.subscriptionPlan) === SubscriptionPlanIds.clinic;
}

/** Agenda calendar chips show the professional name only for Multi-capacity plans in an active org context. */
export function shouldShowAgendaEventTherapistName(user?: IUser | null): boolean {
    const capabilities = resolveSeatCapabilities(user);
    return capabilities.isMultiCapacityPlan && Boolean(user?.activeClinicId || user?.clinic?.id);
}

export function isClinicManager(user?: IUser | null): boolean {
    return Boolean((user?.activeClinicId || user?.clinic?.id) && user?.clinicRoles?.includes(ClinicRoles.Manager));
}

/**
 * Profissional nav Financeiro desk: solo therapists keep plan gating elsewhere.
 * Clinic members only when Administrador (Manager) or Financeiro (Finance).
 */
export function canAccessTherapistFinanceNav(
    user?: IUser | null,
    options?: { activeClinicId?: string | null },
): boolean {
    const activeClinicId = options?.activeClinicId ?? user?.activeClinicId;
    const inClinic = hasClinicMembership(user) || Boolean(activeClinicId);
    if (!inClinic) {
        return true;
    }

    return isClinicManager(user) || Boolean(user?.clinicRoles?.includes(ClinicRoles.Finance));
}

/**
 * Org-billed Clínica (Multi): user plan slug Clinic after org checkout, plus a
 * payable `/me` clinic status. Default-tenant `clinic.subscriptionStatus === "active"`
 * from ClinicSync is tenancy, not a `clinic_subscriptions` row.
 */
export function hasActivePaidClinicBilling(
    user?: Pick<IUser, "clinic" | "subscriptionPlan"> | null,
): boolean {
    if (normalizePlanSlug(user?.subscriptionPlan) !== SubscriptionPlanIds.clinic) {
        return false;
    }

    const status = user?.clinic?.subscriptionStatus;
    return status === "active" || status === "cancel_at_period_end" || status === "past_due";
}

/** True when `/me` clinic carries a still-future Mivo admin-extension signal. */
export function hasFutureMivoTeamClinicExtension(
    user?: Pick<IUser, "clinic"> | null,
    nowMs: number = Date.now(),
): boolean {
    const raw = user?.clinic?.mivoTeamExtensionEndsAt;
    if (!raw) {
        return false;
    }

    const endsAtMs = Date.parse(raw);
    return Number.isFinite(endsAtMs) && endsAtMs > nowMs;
}


/**
 * @deprecated Use {@link hasActivePaidClinicBilling}.
 */
export function hasActiveClinicSubscription(
    user?: Pick<IUser, "clinic" | "subscriptionPlan"> | null,
): boolean {
    return hasActivePaidClinicBilling(user);
}

/** Main manager on Multi who still needs org billing activation (not during open unpaid catalog trial). */
export function isBillingActivationPending(user?: IUser | null): boolean {
    const capabilities = resolveSeatCapabilities(user);
    if (!(isClinicManager(user) && capabilities.allowsSeatAddons && !hasActivePaidClinicBilling(user))) {
        return false;
    }

    // Open Multi trial (published TrialDays) — do not force Assinatura checkout (parity with Básico/Pro).
    if (typeof user?.trialDaysRemaining === "number" && user.trialDaysRemaining > 0) {
        return false;
    }

    return true;
}

/**
 * @deprecated Use {@link isBillingActivationPending}.
 */
export function isClinicManagerPendingMultiCheckout(user?: IUser | null): boolean {
    return isBillingActivationPending(user);
}

/**
 * Solo billing nav removed — canonical Assinatura is {@link CLINIC_SUBSCRIPTION_PATH}.
 */
export function shouldShowSoloBillingInProfissionalNav(_user?: IUser | null): boolean {
    return false;
}

/** Multi manager nav (/multi/*) requires manager role on active clinic. */
export function canAccessMultiManagerNav(user?: IUser | null): boolean {
    return isClinicManager(user);
}

export function isMultiManagerRoute(pathname: string): boolean {
    const normalized = pathname.replace(/\/+$/, "");
    return (
        normalized === "/multi"
        || normalized.startsWith("/multi/")
        || normalized === "/clinica"
        || normalized.startsWith("/clinica/")
    );
}

export function resolveMarketplacePath(user?: IUser | null, moduleSlug?: string): string {
    const query = moduleSlug ? `?module=${encodeURIComponent(moduleSlug)}` : "";
    if (isClinicManager(user)) {
        return `${CLINIC_MARKETPLACE_PATH}${query}`;
    }
    return `${THERAPIST_MARKETPLACE_PATH}${query}`;
}

/** Clinic members manage staff in Gestão Clínica Equipe (**BR-55**). Leftover consultório page only when there is no clinic. */
export function resolveAdministrativeTeamPath(user?: IUser | null): string {
    if (isClinicManager(user)) {
        return CLINIC_TEAM_PATH;
    }
    if (hasClinicMembership(user)) {
        return "/profissional";
    }
    return THERAPIST_ADMINISTRATIVE_TEAM_PATH;
}

export function hasClinicMembership(user?: IUser | null): boolean {
    return Boolean(user?.activeClinicId || user?.clinic?.id);
}

export function shouldShowClinicMemberMarketplaceCta(user?: IUser | null): boolean {
    if (!user?.activeClinicId && !user?.clinic?.id) {
        return true;
    }
    return canAccessMultiManagerNav(user);
}

export function clinicMemberMarketplaceUpsellDescription(user?: IUser | null): string | undefined {
    if ((user?.activeClinicId || user?.clinic?.id) && !isClinicManager(user)) {
        return "Peça ao administrador da organização para ativar este módulo no Marketplace Multi.";
    }
    return undefined;
}
